// import firebase from "firebase/compat";
import { Timestamp } from 'firebase/firestore';
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, ButtonToolbar, CardBody, Col } from "reactstrap";
import Toast, { toast } from "shared/components/Toast";
import { Controller, useForm } from "react-hook-form";
import FormField from "containers/Account/Register/sharedRegisterComponents/FormField";
import { Checkbox, FormControlLabel } from "@mui/material";
import tableFunctions from "shared/other/tableFunctions";
import { useMemo } from "react";
import ReactDataTable from "shared/components/dataTable/ReactDataTable";
import dateUtils, { DATE_FORMATS } from "utils/dateUtils";
import { validateEmail } from "utils";
import { routes } from "containers/App/Router";
import { getCityById } from "services/cities";
import { getOrganizationById } from "services/organizations";
import ShareLink from "shared/components/ShareLink";
import {
  completelyDeleteExternalUser,
  createPendingExternalUser,
  deleteExternalUser,
  getAllExternalUser,
  setExternalUserData,
  checkEmailAvailability,
} from "services/users";
import { sendEmail } from "services/messaging";
import { useEffect } from "react";
import {
  isUserCitySelector,
  isUserOrganisationSelector,
} from "redux/selectors/user";
import { useSelector } from "react-redux";
import { useAuth } from "shared/providers/AuthProvider";

const Wrapper = styled.div`
  padding: 30px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const isAccessAccountVisible = (user, { isCity, isOrganisation, entityId, currentUserId }) => {
  if (currentUserId && user.createdBy === currentUserId) {
    return true;
  }

  if (isOrganisation) {
    return user.organisationId === entityId;
  }

  if (isCity) {
    // Les comptes org héritent souvent du cityId : ne garder que ceux créés pour la ville.
    return (
      user.cityId === entityId &&
      !user.organisationId &&
      user.externalFor !== "organisation"
    );
  }

  return false;
};

const mergeExternalUsersById = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((user) => {
    if (user?.id) {
      byId.set(user.id, user);
    }
  });
  return [...byId.values()];
};

const AccessSettings = ({ details, cityId }) => {
  const { t, i18n } = useTranslation("common");
  const [users, setUsers] = useState([]);
  const [userId] = useAuth();
  const isCity = useSelector(isUserCitySelector);
  const isOrganisation = useSelector(isUserOrganisationSelector);

  const entityId = useMemo(() => {
    if (isOrganisation) {
      return details?.organisationId || details?.id || userId;
    }
    if (isCity) {
      return details?.cityId || details?.id || cityId || userId;
    }
    return details?.id || cityId || userId;
  }, [cityId, details?.cityId, details?.id, details?.organisationId, isCity, isOrganisation, userId]);

  const [errors, setErrors] = useState({});
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      email: "",
      canEdit: false,
    },
  });

  useEffect(() => {
    const fillUsers = async () => {
      if (!entityId) {
        setUsers([]);
        return;
      }

      const entityCondition = isCity
        ? { cityId: entityId }
        : isOrganisation
          ? { organisationId: entityId }
          : null;

      if (!entityCondition) {
        setUsers([]);
        return;
      }

      const [pendingByEntity, confirmedByEntity, pendingByCreator, confirmedByCreator] =
        await Promise.all([
          getAllExternalUser("pending", entityCondition),
          getAllExternalUser("confirmed", entityCondition),
          userId ? getAllExternalUser("pending", { createdBy: userId }) : Promise.resolve([]),
          userId ? getAllExternalUser("confirmed", { createdBy: userId }) : Promise.resolve([]),
        ]);

      const mergedUsers = mergeExternalUsersById(
        pendingByEntity.map((user) => ({ ...user, status: "pending" })),
        confirmedByEntity.map((user) => ({ ...user, status: "confirmed" })),
        pendingByCreator.map((user) => ({ ...user, status: "pending" })),
        confirmedByCreator.map((user) => ({ ...user, status: "confirmed" }))
      );

      setUsers(
        mergedUsers.filter((user) =>
          isAccessAccountVisible(user, {
            isCity,
            isOrganisation,
            entityId,
            currentUserId: userId,
          })
        )
      );
    };
    fillUsers();
  }, [entityId, isCity, isOrganisation, userId]);

  const handleSubmitForm = useCallback(
    async (values) => {
      try {
        const validEmail = validateEmail(values.email);
        if (!validEmail) {
          setErrors({
            email: {
              fr: "Adresse e-mail invalide",
              en: "Invalid email address",
            },
          });
          return;
        }

        const normalizedEmail = values.email.trim().toLowerCase();
        const alreadyListed = users.some(
          (user) => user.email?.trim().toLowerCase() === normalizedEmail
        );

        if (alreadyListed) {
          toast.error(t("admin.email_already_exists"));
          return;
        }

        const emailAvailability = await checkEmailAvailability(values.email);
        if (!emailAvailability?.available) {
          toast.error(t("admin.email_already_exists"));
          return;
        }

        const shouldInheritOrganisationAccess = details.role === "organisation";
        const entityDetails = isCity
          ? await getCityById(entityId)
          : await getOrganizationById(entityId);

        values.createdOn = Timestamp.now();
        values.createdBy = userId;
        values.externalFor = details.role;
        values[`${details.role}Id`] = entityId;

        if (shouldInheritOrganisationAccess) {
          values.cityId = entityDetails?.cityId || details?.cityId || null;
          values.canEdit = true;
          values.canSeeOthers = true;
          values.isSeenByOthers = true;
          values.seenByOthers = true;
        }

        const newUserId = await createPendingExternalUser(values);
        const url = `${window.location.origin}${routes.register.entity.replace(
          ":entity",
          "confirm"
        )}?${details.role}Id=${entityId}&pendingId=${newUserId}`;

        const senderName = "Admin";
        const from = `${senderName} <info@greenplay.social>`;
        const to = [values.email];

        const entityName = entityDetails?.name || details?.name || "GreenPlay";

        await sendEmail({
          from: from,
          to,
          replyTo: from,
          html: `
            <html>
              Bonjour,<br><br>

              ⚠️❗❗ IMPORTANT : Vous devez vous déconnecter si vous êtes déjà connecté avec le compte administrateur ❗❗⚠️
              <br><br>
              Vous avez été invité·e à accéder à ${entityName}.<br><br>
              Veuillez appuyer sur ce lien pour créer votre accès.<br><br>
              <a href="${url}">${url}</a>
              <br><br>

              Sur cette page, vous pourrez soit créer votre compte avec votre courriel, soit utiliser le SSO Microsoft avec cette même adresse courriel.<br><br>

              Merci

              L'équipe Greenplay/Allons
            </html>
          `,
          subject: `Création de compte ${entityName}`,
        });
        setUsers((prev) => [
          ...prev,
          { id: newUserId, ...values, status: "pending" },
        ]);
        reset({
          email: "",
          canEdit: false,
        });
        toast.success(t("settings.success_create_external_user"));
      } catch (error) {
        const apiData = error?.response?.data?.data;
        if (
          apiData?.available === false ||
          apiData?.reason === "email_already_exists"
        ) {
          toast.error(t("admin.email_already_exists"));
        } else {
          toast.error(t("settings.failed_create_external_user"));
        }
        console.log("error", error);
      }
    },
    [details, entityId, isCity, reset, t, userId, users]
  );

  const columns = useMemo(() => {
    const mainCols = tableFunctions.getUsersAccessListColumnData(t);
    mainCols.push({
      accessor: "actions",
      Cell: (props) => {
        return (
          <ActionsWrapper>
            <Button
              size="sm"
              color="link"
              style={{ margin: 0, background: "transparent" }}
              onClick={async () => {
                const foundedUser = users.find(
                  (user) => user.id === props.row.original.id
                );
                await setExternalUserData({
                  userId: props.row.original.id,
                  data: { canEdit: !foundedUser.canEdit },
                  status: props.row.original.status,
                });
                setUsers((prev) =>
                  prev.map((user) =>
                    user.id === props.row.original.id
                      ? { ...user, canEdit: !user.canEdit }
                      : user
                  )
                );
              }}
            >
              {t("global.edit")}
            </Button>
            <Button
              size="sm"
              color="danger"
              style={{ margin: 0 }}
              onClick={async () => {
                if (props.row.original.status === "pending") {
                  await deleteExternalUser(
                    props.row.original.id,
                    props.row.original.status
                  );
                } else {
                  await completelyDeleteExternalUser(props.row.original.id)
                }
                setUsers((prev) =>
                  prev.filter((user) => user.id !== props.row.original.id)
                );
              }}
            >
              {t("global.delete")}
            </Button>
          </ActionsWrapper>
        );
      },
    });
    return mainCols;
  }, [t, users]);

  const rows = useMemo(() => {
    return users.map((row, index) => {
      const today = new Date();

      const createdOn =
        typeof row.createdOn === "number"
          ? dateUtils.formatDate(row !== undefined ?
            new Date(row.createdOn) : today,
            DATE_FORMATS.DAY_MM_DD
          )
          : dateUtils.formatDate(
            (row !== undefined ? row.createdOn.toDate() : today),
            DATE_FORMATS.DAY_MM_DD
          );
      return {
        key: index + 1,
        email: row.email,
        canEdit: row.canEdit,
        canSeeOthers: row.canSeeOthers,
        isSeenByOthers: row.isSeenByOthers,
        createdOn: createdOn,
        id: row.id,
        status: row.status,
      };
    });
  }, [users]);

  const organisationRegisterLink = useMemo(() => {
    if (!cityId) {
      return "";
    }

    return `${window.location.origin}${routes.register.entity.replace(
      ":entity",
      "organisation"
    )}?cityId=${cityId}`;
  }, [cityId]);

  return (
    <Wrapper>
      {isCity && organisationRegisterLink ? (
        <div style={{ marginBottom: 24 }}>
          <ShareLink link={organisationRegisterLink} entity="organisation" />
        </div>
      ) : null}
      <Col md={12} lg={12}>
        <div className="card__title">
          <h4 className="bold-text">{t("settings.create_account")}</h4>
        </div>
        <CardBody>
          <form className="form row" onSubmit={handleSubmit(handleSubmitForm)}>
            <div className="form__form-group col-md-6">
              <span className="form__form-group-label">Email</span>
              <div className="form__form-group-field">
                <Controller
                  name={"email"}
                  control={control}
                  render={({ field }) => (
                    <>
                      <FormField
                        label={t("account.profile.email")}
                        type={"text"}
                        placeholder={"example@mail.com"}
                        value={field.value}
                        onFocus={() => { }}
                        autocomplete={"email"}
                        onBlur={() => { }}
                        onChange={field.onChange}
                        error={
                          errors &&
                          errors[field.name] &&
                          errors[field.name][i18n.language]
                        }
                      />
                    </>
                  )}
                />
              </div>
              <div className="form__form-group-field">
                <Controller
                  name="canEdit"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label={t("settings.can_edit_label")}
                    />
                  )}
                />
              </div>
            </div>
            <ButtonToolbar className="form__button-toolbar col-md-12">
              <Button color="primary ml-auto" type="submit">
                {t("forms.submit")}
              </Button>
            </ButtonToolbar>
          </form>
        </CardBody>
        {users.length > 0 && (
          <ReactDataTable
            columns={columns}
            rows={rows}
            pageSize={10}
          />
        )}
      </Col>
      <Toast />
    </Wrapper>
  );
};

export default AccessSettings;
